import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Award, Lock, FileText, Headphones, CheckCircle2, XCircle } from "lucide-react";
import { Nav, Footer } from "./Nav";
import { api, API } from "../api";
import { useAuth } from "../context/AuthContext";

const ICONS = { manual: FileText, workbook: FileText, trainer: FileText, audio: Headphones };

export function Study() {
  const { courseId } = useParams();
  const { user, openAuth } = useAuth();
  const [ov, setOv] = useState(null);
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [written, setWritten] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/study/${courseId}`).then((r) => setOv(r.data)).catch(() => setOv(false));
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, courseId]);

  const download = async (key, title) => {
    try {
      const r = await api.get(`/study/${courseId}/download/${key}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Ely Wellbeing - ${ov.award} - ${title}.docx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed — are you still signed in?"); }
  };

  const startTest = async () => {
    try { const r = await api.get(`/study/${courseId}/test`); setTest(r.data); setResult(null); }
    catch { toast.error("Could not load the test."); }
  };

  const submit = async () => {
    if (Object.keys(answers).length < test.mcq.length) { toast.error("Please answer every multiple-choice question."); return; }
    setBusy(true);
    try {
      const payload = { answers: test.mcq.map((_, i) => (answers[i] ?? -1)), written: test.written.map((_, i) => written[i] || "") };
      const r = await api.post(`/study/${courseId}/test/submit`, payload);
      setResult(r.data);
      if (r.data.passed) { toast.success(`Passed with ${r.data.score}%! Certificate issued.`); load(); }
      else toast.error(`Scored ${r.data.score}%. Pass mark is ${r.data.pass_mark}% — review and try again.`);
    } catch { toast.error("Submission failed."); } finally { setBusy(false); }
  };

  const certUrl = (cid) => `${API}/certificate/${cid}/pdf`;

  return (
    <>
      <Nav />
      <section className="hero" style={{ padding: "8rem 0 2rem" }}>
        <div className="wrap">
          <span className="badge-gold">STUDY & CERTIFY</span>
          <h1 style={{ marginTop: "1rem", fontSize: "clamp(1.8rem,3.5vw,3rem)" }} data-testid="study-title">
            {ov && ov.title ? ov.title : "Your Course"}
          </h1>
        </div>
      </section>

      <section className="sec pad" style={{ paddingTop: "1rem" }}>
        <div className="wrap" style={{ display: "grid", gap: "1.5rem" }}>
          {!user && (
            <div className="band" style={{ background: "var(--panel)" }} data-testid="study-signin">
              <Lock size={22} /><h2 style={{ color: "#fff" }}>Please sign in</h2>
              <p className="muted">Sign in to access your course materials and certification.</p>
              <button className="btn btn-primary" onClick={() => openAuth("login")} data-testid="study-signin-btn">Sign in</button>
            </div>
          )}

          {user && ov === false && <p className="muted">Course not found.</p>}

          {user && ov && !ov.has_access && (
            <div className="band" style={{ background: "var(--panel)" }} data-testid="study-locked">
              <Lock size={22} /><h2 style={{ color: "#fff" }}>You're not enrolled yet</h2>
              <p className="muted">Enrol in this course to unlock the manuals, workbook, audio and certification.</p>
              <Link className="btn btn-gold" to="/learn">Browse courses</Link>
            </div>
          )}

          {user && ov && ov.has_access && (
            <>
              <div data-testid="study-materials">
                <div className="row-head gold"><span className="bar" /><h2>Course Materials</h2><span className="tagline">Download your manuals and audio.</span></div>
                <div className="grid">
                  {ov.materials.map((m) => {
                    const Icon = ICONS[m.key] || FileText;
                    return (
                      <button key={m.key} className="prac-card" style={{ cursor: "pointer", textAlign: "left" }}
                        onClick={() => download(m.key, m.title)} data-testid={`material-${m.key}`}>
                        <span className="p-av"><Icon size={18} /></span>
                        <div><strong>{m.title}</strong><div className="muted" style={{ fontSize: ".85rem" }}>Word document · click to download</div></div>
                        <Download size={18} style={{ marginLeft: "auto" }} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {ov.certificate && (
                <div className="band green" data-testid="study-certified">
                  <Award size={26} color="#fff" />
                  <h2 style={{ color: "#fff" }}>You're certified! 🎉</h2>
                  <p style={{ color: "#fff", opacity: .9 }}>{ov.award} · Certificate {ov.certificate.certificate_id} · Score {ov.certificate.score}%</p>
                  <a className="btn btn-gold" href={certUrl(ov.certificate.certificate_id)} target="_blank" rel="noreferrer" data-testid="download-cert-btn">
                    <Award size={16} /> Download Certificate (PDF)
                  </a>
                </div>
              )}

              {ov.test_locked_until && !ov.certificate && (
                <div className="band" style={{ background: "var(--panel)" }} data-testid="study-test-locked">
                  <Lock size={22} />
                  <h2 style={{ color: "#fff" }}>Your certification test unlocks soon</h2>
                  <p className="muted">
                    The test opens on <strong style={{ color: "#f0c869" }}>{new Date(ov.test_locked_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</strong> — six months after you enrolled.
                    Use this time to work through your manuals and learn-on-the-go audio; you'll be more than ready.
                  </p>
                </div>
              )}

              {ov.test_available && !ov.certificate && (
                <div data-testid="study-test">
                  <div className="row-head red"><span className="bar" /><h2>Certification Test</h2><span className="tagline">Pass mark {ov.certificate ? "" : ""}80%.</span></div>
                  {!test && <button className="btn btn-primary" onClick={startTest} data-testid="start-test-btn">Start the test</button>}
                  {test && (
                    <div style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}>
                      {test.mcq.map((q, i) => (
                        <div key={i} className="prac-card" style={{ display: "block" }} data-testid={`q-${i}`}>
                          <strong>{i + 1}. {q.q}</strong>
                          <div style={{ display: "grid", gap: ".3rem", marginTop: ".5rem" }}>
                            {q.options.map((o, j) => (
                              <label key={j} style={{ display: "flex", gap: ".5rem", cursor: "pointer" }}>
                                <input type="radio" name={`q${i}`} checked={answers[i] === j}
                                  onChange={() => setAnswers((a) => ({ ...a, [i]: j }))} data-testid={`q-${i}-opt-${j}`} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      <h3 style={{ color: "var(--green)", margin: ".4rem 0 0" }}>Written reflections (for your trainer)</h3>
                      {test.written.map((w, i) => (
                        <div key={i} className="prac-card" style={{ display: "block" }}>
                          <strong>{w}</strong>
                          <textarea rows={3} style={{ width: "100%", marginTop: ".5rem", borderRadius: 8, padding: ".6rem", border: "1px solid var(--line)" }}
                            value={written[i] || ""} onChange={(e) => setWritten((x) => ({ ...x, [i]: e.target.value }))}
                            data-testid={`written-${i}`} placeholder="Type your answer…" />
                        </div>
                      ))}
                      <button className="btn btn-primary" disabled={busy} onClick={submit} data-testid="submit-test-btn">
                        {busy ? "Marking…" : "Submit test"}
                      </button>
                    </div>
                  )}
                  {result && (
                    <div className="band" style={{ marginTop: "1rem", background: result.passed ? "rgba(62,142,65,.15)" : "rgba(176,57,43,.12)" }} data-testid="test-result">
                      {result.passed ? <CheckCircle2 color="#3E8E41" size={26} /> : <XCircle color="#B0392B" size={26} />}
                      <h2 style={{ color: "#fff" }}>You scored {result.score}% ({result.correct}/{result.total})</h2>
                      <p className="muted">{result.passed ? "Congratulations — you passed! Your certificate has been issued." : `Pass mark is ${result.pass_mark}%. Review the manual and try again.`}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
